import { getGetDeviceGroupsKey, useUpdateDeviceGroup } from 'gen/client';
import { useCallback, useEffect, useReducer, useRef } from 'react';
import { mutate } from 'swr';

import { UIInstance } from '@ui/Types/Address';
import { customSuccessToast, deviceCreatedSuccess } from '@ui/Utils/toasts';

import { discoveryReducer, initialDiscoveryState } from './discoveryReducer';
import { DiscoveryPhase, ScanStrategy, SubmitDetailsPayload } from './types';

interface UseDeviceDiscoveryArgs {
    autoPrompt: boolean;
    installationID: string | null;
    isNewDevice: boolean;
    strategy: ScanStrategy;
    suppressInstallation: (installationID: string) => void;
}

export function useDeviceDiscovery({
    autoPrompt,
    installationID,
    isNewDevice,
    strategy,
    suppressInstallation,
}: UseDeviceDiscoveryArgs) {
    const [state, dispatch] = useReducer(discoveryReducer, initialDiscoveryState);
    const abortRef = useRef<AbortController | null>(null);
    const { trigger: updateDeviceGroup } = useUpdateDeviceGroup(state.discovered?.groupId || '', {});

    useEffect(() => {
        dispatch({
            payload: { autoPromptEligible: !!installationID && isNewDevice && autoPrompt, installationID, isNewDevice },
            type: 'INIT',
        });
    }, [installationID, isNewDevice, autoPrompt]);

    const startScan = useCallback(
        (instance: UIInstance | null) => {
            // Re-entry guard
            if (state.phase === DiscoveryPhase.Scanning) {
                return;
            }
            if (abortRef.current) {
                abortRef.current.abort();
            }
            const ctrl = new AbortController();
            abortRef.current = ctrl;
            dispatch({ payload: { instanceId: instance?.id || null }, type: 'START_SCAN' });
            // Minimal scanning indicator; no progress tracking beyond initial
            dispatch({ payload: { progress: 0 }, type: 'SCAN_PROGRESS' });
            void strategy
                .start(ctrl.signal, { instance, instanceId: instance?.id || null }, (p) =>
                    dispatch({
                        payload: { progress: p.progress },
                        type: 'SCAN_PROGRESS',
                    }),
                )
                .then((res) => {
                    if (!ctrl.signal.aborted) {
                        dispatch({
                            payload: { devicespecId: res.devicespecId, groupId: res.groupId, raw: res.raw },
                            type: 'SCAN_RESULT',
                        });
                    }
                })
                .catch((error: unknown) => {
                    if (!ctrl.signal.aborted) {
                        dispatch({
                            payload: { error: error instanceof Error ? error.message : 'Unknown error' },
                            type: 'SCAN_ERROR',
                        });
                    }
                });
        },
        [strategy, state.phase],
    );

    const cancel = useCallback(() => {
        if (abortRef.current) {
            abortRef.current.abort();
        }
        dispatch({ type: 'CANCEL' });
    }, []);

    const submit = useCallback(
        async (payload: SubmitDetailsPayload) => {
            if (state.phase !== DiscoveryPhase.Found || !state.discovered || state.submitting) {
                return;
            }
            dispatch({ type: 'SUBMIT_START' });
            try {
                await updateDeviceGroup({
                    description: payload.description,
                    group_name: payload.name,
                    location: payload.location,
                    visible: payload.visible,
                });
                await mutate(getGetDeviceGroupsKey());
                customSuccessToast(deviceCreatedSuccess);
                if (state.installationID) {
                    suppressInstallation(state.installationID);
                }
                dispatch({ type: 'SUBMIT_SUCCESS' });
            } catch (error) {
                dispatch({
                    payload: { error: error instanceof Error ? error.message : 'Submit failed' },
                    type: 'SCAN_ERROR',
                });
            }
        },
        [
            state.discovered,
            state.installationID,
            state.phase,
            state.submitting,
            suppressInstallation,
            updateDeviceGroup,
        ],
    );

    const reset = useCallback(() => {
        if (abortRef.current) {
            abortRef.current.abort();
        }
        dispatch({ type: 'RESET' });
    }, []);

    // Clear progress interval when leaving scanning or on unmount
    useEffect(() => {
        // No interval cleanup needed after simplification
    }, [state.phase]);

    return {
        cancel,
        isBusy: state.phase === DiscoveryPhase.Scanning || state.submitting,
        phase: state.phase,
        reset,
        showPrompt: () => dispatch({ type: 'SHOW_PROMPT' }),
        startScan,
        state,
        submit,
    };
}
